import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import {
  DEFAULT_SIDEBAR_LAYOUT,
  layoutFromDrag,
  layoutFromKey,
  maxSidebarWidth,
  RAIL_WIDTH,
  type SidebarLayout,
} from '@/lib/sidebar-size'
import { nameSidebarWidth } from '@/hooks/use-sidebar-layout'

/** The root carries this while a drag is under way, so the cursor holds and no text gets selected. */
const RESIZING_CLASS = 'is-resizing-sidebar'

interface Drag {
  pointerId: number
  startX: number
  startWidth: number
  /** The width before the drag, remembered if the drag folds the sidebar to the rail. */
  widthBeforeDrag: number | null
  /** Where the drag has got to; the layout is stored when it ends. */
  latest: SidebarLayout
  /** Whether the sidebar on screen is the rail, which only a render can change. */
  shownCollapsed: boolean
}

interface SidebarResizerProps {
  layout: SidebarLayout
  /** The width the sidebar takes now, and the viewport it is fitted to. */
  width: number
  viewportWidth: number
  setLayout: (layout: SidebarLayout) => void
}

/**
 * The sidebar's right edge as a window splitter (Bilanss SidebarResizer): drag it, step it with the arrow keys, send
 * it to either end with Home and End, fold it with Enter, and double-click it for the default width.
 *
 * While dragging, the new width is written straight to the page root once per frame, so the page reflows at most
 * once a frame and React renders only when the sidebar folds or unfolds.
 */
export function SidebarResizer({ layout, width, viewportWidth, setLayout }: SidebarResizerProps) {
  const drag = useRef<Drag | null>(null)
  const frame = useRef(0)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    document.documentElement.classList.toggle(RESIZING_CLASS, dragging)
  }, [dragging])
  useEffect(
    () => () => {
      cancelAnimationFrame(frame.current)
      document.documentElement.classList.remove(RESIZING_CLASS)
    },
    [],
  )

  function startDrag(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return
    // No text selection and no native drag while the edge moves.
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: width,
      widthBeforeDrag: layout.width,
      latest: layout,
      shownCollapsed: layout.collapsed,
    }
    setDragging(true)
  }

  /** Once a frame: folding or unfolding changes what the sidebar shows, so React renders it; a width only moves. */
  function showLatest() {
    frame.current = 0
    const current = drag.current
    if (!current) return
    const next = current.latest
    if (next.collapsed !== current.shownCollapsed) {
      current.shownCollapsed = next.collapsed
      setLayout(next)
    } else if (!next.collapsed) {
      nameSidebarWidth(next.width)
    }
  }

  function moveDrag(event: PointerEvent<HTMLDivElement>) {
    const current = drag.current
    if (!current || event.pointerId !== current.pointerId) return
    const draggedWidth = current.startWidth + event.clientX - current.startX
    current.latest = layoutFromDrag(draggedWidth, viewportWidth, current.widthBeforeDrag)
    if (!frame.current) frame.current = requestAnimationFrame(showLatest)
  }

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    const current = drag.current
    if (!current || event.pointerId !== current.pointerId) return
    drag.current = null
    cancelAnimationFrame(frame.current)
    frame.current = 0
    setDragging(false)
    // The last frame may not have been shown; the stored layout and the edge on screen must agree.
    const { collapsed, width: finalWidth } = current.latest
    if (!collapsed && finalWidth !== null) nameSidebarWidth(finalWidth)
    setLayout(current.latest)
  }

  function stepWithKey(event: KeyboardEvent<HTMLDivElement>) {
    const next = layoutFromKey(layout, event.key, event.shiftKey, viewportWidth)
    if (!next) return
    event.preventDefault()
    setLayout(next)
  }

  return (
    <div
      className={`sidebar-resizer ${dragging ? 'is-dragging' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-controls="app-sidebar"
      aria-label="Resize sidebar"
      aria-valuemin={RAIL_WIDTH}
      aria-valuemax={maxSidebarWidth(viewportWidth)}
      aria-valuenow={width}
      aria-valuetext={layout.collapsed ? 'Icons only' : `${width} pixels`}
      tabIndex={0}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={stepWithKey}
      onDoubleClick={() => setLayout(DEFAULT_SIDEBAR_LAYOUT)}
    />
  )
}
